import { t } from "../../i18n";
import { Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../../api";
import { asRecord, stringValue } from "../chat/ToolResultView.utils";
import { isMarkdownArtifact } from "./artifact-utils";
import { ArtifactCard } from "./ArtifactCard";
import { GenericResult } from "../chat/GenericResult";
import { ResultLabel } from "../ResultLabel";

export function ArtifactResult({ result }: { result: unknown }) {
  const values = asRecord(result);
  const artifact = asRecord(values?.artifact);
  const savedArtifacts = Array.isArray(values?.artifacts)
    ? values.artifacts.flatMap((item) => {
        const value = asRecord(item);
        return value ? [value] : [];
      })
    : [];
  const content = stringValue(values?.content);
  const preview = stringValue(values?.preview);
  const artifactIsMarkdown = artifact
    ? isMarkdownArtifact(
        stringValue(artifact.name),
        stringValue(artifact.mimeType),
      )
    : false;
  const operation = stringValue(values?.operation);
  const artifactId = stringValue(artifact?.id);
  const [savedArtifactId, setSavedArtifactId] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const artifactIsSaved =
    artifact?.saved === true || savedArtifactId === artifactId;
  const isDocumentOperation =
    operation === "create" ||
    operation === "edit" ||
    operation === "export_pdf";
  useEffect(() => {
    if (!isDocumentOperation || !artifactId || artifact?.saved === true) return;
    let active = true;
    void api<{ saved: boolean }>(
      `/artifacts/${encodeURIComponent(artifactId)}/meta`,
    )
      .then((view) => {
        if (active && view.saved) setSavedArtifactId(artifactId);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [artifact?.saved, artifactId, isDocumentOperation]);
  const save = async () => {
    if (!artifactId || saving) return;
    setSaving(true);
    setSaveError("");
    try {
      await api(`/artifacts/${encodeURIComponent(artifactId)}/save`, {
        method: "POST",
      });
      setSavedArtifactId(artifactId);
    } catch (cause) {
      setSaveError(
        cause instanceof Error ? cause.message : "Artifact could not be saved",
      );
    } finally {
      setSaving(false);
    }
  };
  const heading =
    operation === "render"
      ? "Document preview"
      : operation === "edit"
        ? artifactIsSaved
          ? "Document updated in your vault"
          : "Document updated in this chat"
        : operation === "create" || operation === "export_pdf"
          ? artifactIsSaved
            ? "Artifact saved to your vault"
            : "Artifact ready to save"
          : operation === "list"
            ? "Your artifact vault"
            : "Artifact ready to reuse";
  return (
    <div className="space-y-2">
      {(artifact || savedArtifacts.length > 0) && (
        <div className="flex items-center gap-2 px-1 text-xs font-semibold text-lumen-800 dark:text-lumen-200">
          <Sparkles size={14} /> {heading}
        </div>
      )}
      {artifact && (
        <ArtifactCard
          artifact={{ ...artifact, previewContent: content || preview }}
          featured={operation === "create" && savedArtifacts.length === 0}
          onSave={
            isDocumentOperation && !artifactIsSaved
              ? () => void save()
              : undefined
          }
          saving={saving}
        />
      )}
      {saveError && (
        <div className="px-1 text-xs text-red-600 dark:text-red-300">
          {saveError}
        </div>
      )}
      {savedArtifacts.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-semibold text-stone-500 dark:text-stone-300">
            {t("artifact", { count: savedArtifacts.length })}
          </div>
          {savedArtifacts.map((item) => (
            <ArtifactCard key={stringValue(item.id)} artifact={item} />
          ))}
        </div>
      )}
      {content && !artifactIsMarkdown && (
        <div className="rounded-xl border border-stone-200 bg-white/70 p-3 dark:border-white/10 dark:bg-white/[0.03]">
          <ResultLabel>{t("Artifact content")}</ResultLabel>
          <pre className="max-h-80 overflow-auto text-xs leading-5 break-words whitespace-pre-wrap text-stone-600 dark:text-stone-300">
            {content}
          </pre>
        </div>
      )}
      {!artifact && !savedArtifacts.length && !content && (
        <GenericResult value={result} />
      )}
    </div>
  );
}
